import Grid from "@material-ui/core/Grid";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { formatDateTime } from "../../../utils/Formatter";
import { EditableField } from "../../../components/EditableField";
import { makeStyles, TextField } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CameraRepository from "../../../repositories/CameraRepository";
import StaticClassifierRepository from "../../../repositories/StaticClassifierRepository";
import { FullImagePreview } from "../../../components/FullImagePreview";
import Button from "@material-ui/core/Button";
import DeleteIcon from "@material-ui/icons/Delete";
import { useConfirm } from "material-ui-confirm";

const useStyles = makeStyles(() => ({
  listItem: {
    paddingTop: "0px",
    paddingBottom: "0px",
  },
  deleteBtn: {
    color: "red",
    marginLeft: 0,
    marginTop: 16,
  },
}));

const Details = ({ eventData, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const [changes, setChanges] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [directions, setDirections] = useState([]);
  const [cameras, setCameras] = useState([]);

  const cameraRepository = new CameraRepository();
  const classifierRepository = new StaticClassifierRepository();
  const confirm = useConfirm();

  const formatDirectionType = (value) => {
    return t(classifierRepository.getMovingDirectionNameById(value));
  };

  const classes = useStyles();

  useEffect(async () => {
    if (!eventData.CameraId) {
      const camerasRs = await cameraRepository.getSelectorWithParkingZone();
      setCameras(camerasRs.data);
    }

    setDirections(classifierRepository.getAllMovingDirections());

    return () => {};
  }, [eventData]);

  useEffect(() => {
    onEdit({ changes: changes });

    return () => {};
  }, [changes]);

  const handleEditorChange = (e) => {
    let newChanges = null;
    if (e.value != e.originalValue) {
      const newValue = {};
      newValue[e.name] = e.value;
      newChanges = Object.assign({}, changes, newValue);
    } else {
      if (changes != null) {
        newChanges = {};
        for (let i in changes) {
          if (i != e.name) {
            newChanges[i] = changes[i];
          }
        }
      }
    }

    let hasChanges = false;
    if (newChanges) {
      for (let i in newChanges) {
        hasChanges = true;
        break;
      }
    }

    setChanges(hasChanges ? newChanges : null);
  };

  const suspiciousText = (value) => {
    return classifierRepository.getCameraEventSuspiciousStatusNameById(value);
  };

  const handleDeleteClick = () => {
    confirm({ description: t("Do you really want to delete event ?") })
      .then(async () => {
        onDelete();
      })
      .catch(() => {});
  };

  const deleteAllowed = () => {
    return eventData && eventData.PairId == null;
  };

  return (
    <>
      {imagePreview && (
        <FullImagePreview
          src={imagePreview}
          onClick={(e) => setImagePreview(null)}
        />
      )}
      <Grid container>
        <Grid item xl={4} xs={12}>
          <List className="condensedList">
            <ListItem>
              <ListItemText primary={t("Id")} secondary={eventData.Id} />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={t("GUID")}
                secondary={eventData.ExternalId}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={t("Event date")}
                secondary={formatDateTime(eventData.EventDate)}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={t("Parking zone")}
                secondary={
                  eventData.ParkingZoneName ? eventData.ParkingZoneName : "-"
                }
              />
            </ListItem>
            <ListItem>
              <EditableField
                name="CameraId"
                value={eventData.CameraId}
                textValue={eventData.CameraId ? eventData.CameraCode : "-"}
                label={t("Camera")}
                allowEdit={!eventData.CameraId}
                onChange={handleEditorChange}
                editor={({ label, value, onChange }) => {
                  return (
                    <TextField
                      label={label}
                      select
                      fullWidth
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      value={value || null}
                      onChange={onChange}
                      variant="outlined"
                    >
                      <option></option>
                      {cameras.map((cameraGroup, i) => {
                        return (
                          <optgroup key={i} label={cameraGroup.ParkingZoneName}>
                            {cameraGroup.Cameras.map((camera, j) => {
                              return (
                                <option key={camera.Id} value={camera.Id}>
                                  {camera.Name}
                                </option>
                              );
                            })}
                          </optgroup>
                        );
                      })}
                    </TextField>
                  );
                }}
              />
            </ListItem>
            <ListItem>
              <EditableField
                name="DirectionType"
                value={eventData.DirectionType}
                textValue={formatDirectionType(eventData.DirectionType)}
                label={t("Direction")}
                allowEdit={true}
                onChange={handleEditorChange}
                editor={({ label, value, onChange }) => {
                  return (
                    <TextField
                      label={label}
                      select
                      fullWidth
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      value={value || null}
                      onChange={onChange}
                      variant="outlined"
                    >
                      {directions.map((item) => {
                        return (
                          <option key={item.id} value={item.id}>
                            {t(item.name)}
                          </option>
                        );
                      })}
                    </TextField>
                  );
                }}
              />
            </ListItem>
            <ListItem>
              <Grid item xs={5}>
                <EditableField
                  name="NumberPlateOrigin"
                  value={eventData.NumberPlateOrigin}
                  textValue={eventData.NumberPlateOrigin}
                  label={t("Number plate origin")}
                  onChange={handleEditorChange}
                  editor={({ label, value, onChange }) => {
                    return (
                      <TextField
                        label={label}
                        fullWidth
                        defaultValue={value}
                        onChange={onChange}
                        variant="outlined"
                      />
                    );
                  }}
                />
              </Grid>
              <Grid item xs={1}></Grid>
              <Grid item xs={6}>
                <EditableField
                  name="NumberPlate"
                  value={eventData.NumberPlate}
                  textValue={eventData.NumberPlate}
                  label={t("Number plate")}
                  onChange={handleEditorChange}
                  editor={({ label, value, onChange }) => {
                    return (
                      <TextField
                        label={label}
                        fullWidth
                        defaultValue={value}
                        onChange={onChange}
                        variant="outlined"
                      />
                    );
                  }}
                />
              </Grid>
            </ListItem>
          </List>
        </Grid>
        <Grid item xl={3} xs={12}>
          <List className="condensedList">
            <ListItem>
              <ListItemText
                primary={t("Ocr confidence")}
                secondary={eventData.OcrConfidence}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={t("Paired event")}
                secondary={eventData.PairId ? eventData.PairId : "-"}
              />
            </ListItem>
            {eventData.Suspicious && (
              <ListItem>
                <ListItemText
                  primary={t("Suspicious")}
                  secondary={suspiciousText(eventData.Suspicious)}
                />
              </ListItem>
            )}
            {eventData.Suspicious && (
              <ListItem>
                <ListItemText
                  primary={t("Source Id")}
                  secondary={eventData.SourceId}
                />
              </ListItem>
            )}
            {deleteAllowed() && (
              <ListItem>
                <Button
                  variant="contained"
                  className={classes.deleteBtn}
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteClick}
                >
                  {t("Delete")}
                </Button>
              </ListItem>
            )}
            `
          </List>
        </Grid>
        <Grid item xl={5} xs={12}>
          <div>
            <img src={eventData.NumberPlateImageUrl} />
          </div>
          <div>
            <a
              href="#"
              onClick={() => setImagePreview(eventData.EventImageUrl)}
            >
              <img src={eventData.EventImageUrl} className="compressedImg2" />
            </a>
          </div>
        </Grid>
      </Grid>
    </>
  );
};

export default Details;
